import { useCallback, useEffect, useState } from "react";
import { execService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Crown, Loader2 } from "lucide-react";
import {
  useTenantScope, TenantPicker, SelectTenantNotice, ForbiddenNotice, PageHeader, PeriodSelector, KpiTile, DrilldownPanel,
} from "./shared";
import { AiExecutiveSummary } from "./AiExecutiveSummary";
import { DailyActionPlan, ExecutiveAlerts } from "./ExecPanels";
import { MorningDigestPanel } from "./MorningDigestPanel";
import { formatCurrency } from "@/lib/currency";

export default function ExecCeoDashboard() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [drill, setDrill] = useState(null);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setData(null); return; }
    setLoading(true);
    setForbidden(false);
    execService.ceo({ ...params, period })
      .then(setData)
      .catch((e) => {
        if (e.response?.status === 403) setForbidden(true);
        else toast.error(apiErrorMessage(e.response?.data?.detail));
      })
      .finally(() => setLoading(false));
  }, [blocked, params, period]);
  useEffect(() => { load(); }, [load]);

  const onDrill = (metric, label) => setDrill({ metric, label });

  return (
    <div data-testid="exec-ceo-page">
      <PageHeader icon={Crown} title="Executive Command Center"
        subtitle="A single, role-aware view of the business — every KPI is live from canonical data.">
        <PeriodSelector period={period} setPeriod={setPeriod} />
        <TenantPicker {...scope} />
      </PageHeader>

      {forbidden ? <ForbiddenNotice /> : blocked ? <SelectTenantNotice /> : loading && !data ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : data ? (
        <div className="space-y-5">
          <MorningDigestPanel params={params} canRun={true} />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ceo-headline">
            {data.headline.map((t) => <KpiTile key={t.key} tile={t} onDrill={onDrill} />)}
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <AiExecutiveSummary period={period} params={params} />
              <DailyActionPlan params={params} />
            </div>
            <ExecutiveAlerts params={params} onDrill={onDrill} />
          </div>

          {/* pillar quick-glance */}
          <div className="grid md:grid-cols-2 gap-4">
            <PillarCard title="Sales" tiles={data.sales.tiles.slice(0, 4)} onDrill={onDrill} />
            <PillarCard title="Service" tiles={data.service.tiles.slice(0, 4)} onDrill={onDrill} />
            <PillarCard title="Marketing" tiles={data.marketing.tiles.slice(0, 4)} onDrill={onDrill} />
            <PillarCard title="Customers" tiles={data.customer.tiles.slice(0, 4)} onDrill={onDrill} />
          </div>
        </div>
      ) : null}

      {drill && <DrilldownPanel metric={drill.metric} label={drill.label} params={params} onClose={() => setDrill(null)} />}
    </div>
  );
}

function PillarCard({ title, tiles, onDrill }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={`pillar-${title.toLowerCase()}`}>
      <h3 className="text-sm font-bold text-foreground mb-3">{title}</h3>
      <div className="grid grid-cols-2 gap-2">
        {tiles.map((t) => (
          <button key={t.key} onClick={t.drilldown ? () => onDrill(t.drilldown, t.label) : undefined}
            className={`bg-muted/40 rounded-lg p-2.5 text-left ${t.drilldown ? "hover:bg-muted cursor-pointer" : ""}`} data-testid={`pillar-kpi-${t.key}`}>
            <p className="text-[10px] text-muted-foreground uppercase">{t.label}</p>
            <p className="text-base font-bold text-foreground">{t.format === "currency" ? formatCurrency(t.value) : t.format === "number" ? new Intl.NumberFormat().format(t.value) : t.format === "percent" ? `${t.value}%` : t.value}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
